<script lang="ts" setup>
import type { CellType, CellItem } from './calendar.type'

import { sdCalendarPanelProps } from './calendar-panel'
import { useDatePrefix        } from './useDatePrefix'
import { computed             } from 'vue'
import { isNumber, dt         } from '@sumdoo/shared'

defineOptions({ name: 'CalendarPanel' })

const props = defineProps(sdCalendarPanelProps)
const emit  = defineEmits(['pick'])

const WEEK_DAYS = ['一', '二', '三', '四', '五', '六', '日']

const { prevMonthDatePrefix$, curMonthDatePrefix$, nextMonthDatePrefix$ } = useDatePrefix(props)

// 取得周起始日
const firstDayOfWeek$ = computed(() => {
    const firstDay = props.firstDayOfWeek
    if (!isNumber(firstDay)) return 1
    if (firstDay === 0     ) return 7

    return firstDay >= 1 && firstDay <= 7 ? firstDay : 1
})

const weekDays$ = computed(() => {
    const firstDay = firstDayOfWeek$.value

    if (firstDay === 1) {
        return WEEK_DAYS.slice()
    } else {
        return WEEK_DAYS.slice(firstDay).concat(WEEK_DAYS.slice(0, firstDay))
    }
})

// 构造日历二维表数据
const rows$ = computed(() => {
    const firstDayOfWeek = firstDayOfWeek$.value
    const date           = props.date
    let firstDayOfMonth  = dt.getFirstDayOfMonth(props.date)
        firstDayOfMonth  = firstDayOfMonth === 0 ? 7 : firstDayOfMonth

    let days = [
        ...dt.getPrevMonthLastDays(date, firstDayOfMonth - firstDayOfWeek).map(date => getCellData('prev', date)),
        ...dt.getCurrMonthDays(date).map(date => getCellData('current', date)),
    ]
    days = days.concat(dt.range(42 - days.length).map((_, index) => getCellData('next', index + 1)))

    return dt.range(days.length / 7).map((_, index) => {
        const start = index * 7
        return days.slice(start, start + 7)
    })
})


 // 构造单元格元数据
function getCellData(type: CellType, date: number) {
    const cell: CellItem = {
        type                     , // 当前日期类型 
        text       : String(date), // 显示内容
        day        : ''          , // YY-MM-DD 日期
        isSelected : false       , // 是否选中
    }
    
    const suffix = `00${date}`.slice(-2)
    switch(type) {
        case 'prev': 
            cell.day  = `${prevMonthDatePrefix$.value}-${suffix}`
            break
        case 'current':
            cell.day = `${curMonthDatePrefix$.value}-${suffix}`
            break
        case 'next':
            cell.day = `${nextMonthDatePrefix$}-${suffix}`
            break
    }   

    // 计算当前日期是否被选中
    if (Array.isArray(props.selectedDay)) {
        cell.isSelected = props.selectedDay.includes(cell.day)
    } else {
        cell.isSelected = props.selectedDay === cell.day
    }

    // 仅显示当月日期，则重置为空
    if (props.onlyShowCurrent && type !== 'current') {
        cell.day        = ''
        cell.text       = ''
        cell.isSelected = false
    }

    return cell
}

const getCellCalss = (cell: CellItem) => {
    const classes = [cell.type] as string[]

    if (cell.isSelected         ) classes.push('is-selected')
    if (cell.day === props.today) classes.push('is-today'   )

    return classes
}

function onPickDay(cell: CellItem) {
    if (props.onlyShowCurrent && cell.type !== 'current') return

    emit('pick', cell)
}

</script>

<template>
    <table class="sd-calendar-panel" cellspacing="0" cellpadding="0">
        <thead>
            <th v-for="weekDay in weekDays$" :key="weekDay">{{ weekDay }}</th>
        </thead>
        <tbody>
            <tr 
                v-for="(tds, tdsIdx) in rows$"
                :key="tdsIdx"
                class="sd-calendar-panel__row"
            >
                <td
                    v-for="(cell, cellIdx) in tds"
                    :key="cellIdx"
                    :class="getCellCalss(cell)"
                    @click="onPickDay(cell)"
                >
                    <div class="sd-calendar-panel__day" :style="[cellStyle ? cellStyle(cell) : '']">
                        <slot name="cell" :data="cell">
                            <span>{{ cell.text }}</span>
                        </slot>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>
